// Today (docs/cockpit/spec.md): the headline counts the decisions and names the biggest; one queue is
// the single source of "needs you", each item with its one-line reason, what kind of money it is, and
// "More about this" for the rest. Review and confirm, or See what's wrong, opens the Work Item's own card right
// here, the same card chat shows with its preview, Confirm and Send back (a message waits out its undo window),
// whichever Channel or person started its Run (#461); only a decision that came without one opens chat. "Open
// the run" opens the item's Run panel. Below the queue, "LAMP noticed" lists the Suggestions. Amber marks only
// the queue: what the Owner must act on.
import { useCallback, useEffect, useId, useRef, useState } from "react";
import {
  ApiError,
  rupees,
  type Api,
  type Card,
  type CardAction,
  type CardForm,
  type Decision,
  type Failure,
  type Suggestion,
  type Today as TodayData,
} from "../api.js";
import { plainAmounts } from "../i18n/format.js";
import { useWords, type Words } from "../i18n/words.js";
import { Suggestions, acceptSuggestion, dismissSuggestion } from "../memory/Memory.js";
import { AskForm } from "../chat/AskForm.js";
import { CardView } from "../chat/CardView.js";
import { UndoEmail, emailOf, type PendingEmail } from "../chat/UndoEmail.js";
import { FailureNotice, nameFailure } from "../preview/Failure.js";
import { usePolling } from "../shell/poll.js";
import { useStepUp } from "../step-up/StepUp.js";

type AmountLabel = NonNullable<Decision["amount"]>["label"];

/**
 * The Owner's decision on an order heading below its quoted margin (6.5.4.1): noted, or, where the customer's change
 * caused it, the rate revision with the customer started. Never a price change by itself.
 */
export const MARGIN_TASK = "6.5.4.1";

/** What kind of money an amount is, in the person's language. */
const amountLabel = (label: AmountLabel, w: Words): string =>
  label === "overdue" ? w.todayAmountOverdue : label === "to bill" ? w.todayAmountToBill : w.todayAmountOrderValue;

/** The headline: how many decisions, and the biggest amount among them with who it is from. */
export function headline(decisions: readonly Decision[], w: Words): string {
  if (decisions.length === 0) return w.todayNothing;
  const biggest = decisions.reduce<Decision | undefined>(
    (max, d) => (d.amount && (!max?.amount || d.amount.paise > max.amount.paise) ? d : max),
    undefined,
  );
  if (!biggest?.amount) return w.todayCount(decisions.length);
  const { paise, label } = biggest.amount;
  return `${w.todayCount(decisions.length)} ${w.todayBiggest(rupees(paise), label, biggest.counterparty?.name)}`;
}

function DecisionItem({
  decision,
  busy,
  onDone,
  onAct,
  onOpen,
  onOpenRun,
  refusal,
}: {
  decision: Decision;
  busy: boolean;
  /** Why the last press on the decision's own card was refused, said on it. */
  refusal?: Failure | undefined;
  onDone: (d: Decision, note?: string, choice?: "rate_revision", form?: CardForm) => void;
  /** An action on the decision's own card, as chat takes it. */
  onAct: (
    d: Decision,
    action: CardAction,
    input?: { reason?: string; text?: string; form?: CardForm },
    card?: Card,
  ) => void;
  onOpen: (d: Decision) => void;
  onOpenRun?: ((run: string) => void) | undefined;
}) {
  const w = useWords();
  const [open, setOpen] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [note, setNote] = useState("");
  const moreId = useId();
  const reviewId = useId();
  const noteId = useId();
  // A Task of the person's own comes with its card only when it is done in a Form of its own (a BOM, a routing), or
  // beside what it reviews (a drawing).
  const ownForm = ["bom", "routing", "drawing_review"].includes(decision.card?.preview?.kind ?? "");
  const card = decision.kind === "do" && !ownForm ? undefined : decision.card;
  const doneInForm = decision.kind === "do" && card !== undefined;
  // A material check waiting for a part's BOM is fixed by giving it, by whoever may: "Give the BOM" (#565).
  const givesBom = card?.bom !== undefined && card.actions.includes("done");
  // A margin at risk is opened as its cost breakdown, or noted with an optional note: nothing changes its price.
  const margin = decision.kind === "do" && decision.task === MARGIN_TASK;
  return (
    <li className="item need">
      <div>
        <h3>{decision.name}</h3>
        {/* What it is about, so two at the same step are told apart (#513): numbers and names as recorded. */}
        {decision.subject ? (
          <p className="subject" translate="no">
            {decision.subject}
          </p>
        ) : null}
        <p className="reason">{plainAmounts(decision.reason)}</p>
        {decision.noOneHolds ? <p className="muted">{decision.noOneHolds.says}</p> : null}
        {/* An incident's own words, so no one confirms or acts on a report without reading it (#1014). */}
        {decision.report && decision.report.length > 0 ? (
          <dl className="report">
            {decision.report.map((line) => (
              <div key={line.field}>
                <dt>{line.label}</dt>
                <dd>{line.text}</dd>
              </div>
            ))}
          </dl>
        ) : null}
        {decision.files && decision.files.length > 0 ? (
          <ul className="overdue" aria-label={w.cardFiles}>
            {decision.files.map((f) => (
              <li key={f.href}>
                <a href={f.href} target="_blank" rel="noopener">
                  {w.cardOpenFile(f.name)}
                </a>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      {decision.amount ? (
        <p className="money">
          {rupees(decision.amount.paise)}
          <span className="kind">{amountLabel(decision.amount.label, w)}</span>
        </p>
      ) : (
        <span />
      )}
      <div className="acts">
        {margin ? (
          <>
            {onOpenRun ? (
              <button type="button" className="btn" onClick={() => onOpenRun(decision.run)}>
                {w.todayOpenBreakdown}
              </button>
            ) : null}
            <label htmlFor={noteId} className="sr-only">
              {w.todayMarginNote}
            </label>
            <input
              id={noteId}
              type="text"
              maxLength={500}
              placeholder={w.todayMarginNote}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <button
              type="button"
              className="btn btn-go"
              disabled={busy}
              onClick={() => onDone(decision, note.trim() === "" ? undefined : note.trim())}
            >
              {w.todayNoteMargin}
            </button>
            {decision.offers?.includes("rate_revision") ? (
              <button
                type="button"
                className="btn"
                disabled={busy}
                onClick={() => onDone(decision, note.trim() === "" ? undefined : note.trim(), "rate_revision")}
              >
                {w.todayStartRateRevision}
              </button>
            ) : null}
          </>
        ) : doneInForm ? (
          <button
            type="button"
            className="btn btn-go"
            aria-expanded={reviewing}
            aria-controls={reviewId}
            onClick={() => setReviewing(!reviewing)}
          >
            {w.todayReview}
          </button>
        ) : decision.kind === "do" && decision.elsewhere ? null : decision.kind === "do" &&
          decision.asks &&
          decision.asks.length > 0 ? (
          <AskForm
            asks={decision.asks}
            done={w.todayMarkDone}
            busy={busy}
            onDone={(form) => onDone(decision, undefined, undefined, form)}
          />
        ) : decision.kind === "do" ? (
          <button type="button" className="btn btn-go" disabled={busy} onClick={() => onDone(decision)}>
            {w.todayMarkDone}
          </button>
        ) : card ? (
          <button
            type="button"
            className="btn btn-go"
            aria-expanded={reviewing}
            aria-controls={reviewId}
            onClick={() => setReviewing(!reviewing)}
          >
            {decision.kind === "confirm" ? w.todayReview : givesBom ? w.bomGive : w.todaySeeWrong}
          </button>
        ) : (
          <button type="button" className="btn btn-go" onClick={() => onOpen(decision)}>
            {decision.kind === "confirm" ? w.todayReview : w.todaySeeWrong}
          </button>
        )}
        <button
          type="button"
          className="linkish"
          aria-expanded={open}
          aria-controls={moreId}
          onClick={() => setOpen(!open)}
        >
          {w.todayMore}
        </button>
        {onOpenRun && !margin ? (
          <button type="button" className="linkish" onClick={() => onOpenRun(decision.run)}>
            {w.todayOpenRun}
          </button>
        ) : null}
        {decision.counterparty?.phone ? (
          <a className="btn" href={`tel:${decision.counterparty.phone.replace(/[^+\d]/g, "")}`}>
            {w.todayCall(decision.counterparty.name)}
          </a>
        ) : null}
      </div>
      <ul id={moreId} className="more" hidden={!open}>
        {decision.details.map((d) => (
          <li key={d}>{plainAmounts(d)}</li>
        ))}
        {decision.codes ? (
          <li className="codes">
            <details>
              <summary>{w.todayCodes}</summary>
              {decision.codes}
            </details>
          </li>
        ) : null}
      </ul>
      {card && reviewing ? (
        <div id={reviewId} className="review">
          <CardView
            card={card}
            busy={busy}
            givingBom={givesBom}
            refusal={refusal}
            onAct={(c, action, input) => onAct(decision, action, input, c)}
          />
        </div>
      ) : null}
    </li>
  );
}

export function Today({
  api,
  onOpenCard,
  onOpenRun,
}: {
  api: Api;
  onOpenCard: (workItem: string) => void;
  /** Opens a Run's panel. */
  onOpenRun?: (run: string) => void;
}) {
  const w = useWords();
  const [today, setToday] = useState<TodayData | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [failure, setFailure] = useState<Failure | undefined>(undefined);
  const [refusedCard, setRefusedCard] = useState<{ workItem: string; failure: Failure } | undefined>(undefined);
  const [remembered, setRemembered] = useState<readonly Suggestion[]>([]);
  const [notice, setNotice] = useState<string | undefined>(undefined);
  const stepUp = useStepUp(api.stepUp);
  const [emailing, setEmailing] = useState<PendingEmail | undefined>(undefined);
  const clearEmailing = useCallback(() => setEmailing(undefined), []);

  /** The latest read of the queue asked for: an earlier one answering after it (a poll) is stale. */
  const asked = useRef(0);
  const load = useCallback(() => {
    const read = ++asked.current;
    api.today().then(
      (loaded) => {
        if (read === asked.current) setToday(loaded);
      },
      () => {
        if (read === asked.current) setProblem(w.todayFailed);
      },
    );
  }, [api, w]);
  useEffect(load, [load]);
  // New Work Items show up without a reload (#512); not while the person is answering one.
  usePolling(() => {
    if (!busy) load();
  });
  // What LAMP noticed is an Owner's; anyone else, or a refusal, just sees none.
  const loadNoticed = useCallback(() => {
    api.memory().then(
      (memory) => setRemembered(memory.suggestions),
      () => setRemembered([]),
    );
  }, [api]);
  useEffect(loadNoticed, [loadNoticed]);
  // Today answers what LAMP noticed too, such as a key account gone quiet; each Suggestion is listed once.
  const noticed = [
    ...(today?.noticed ?? []),
    ...remembered.filter((s) => !today?.noticed?.some((n) => n.key === s.key)),
  ];

  const refused = (error: unknown) =>
    error instanceof ApiError && error.problems.length > 0 ? error.problems.join("; ") : w.todayRefused;

  const answer = async (work: () => Promise<string>) => {
    setBusy(true);
    setNotice(undefined);
    setProblem(undefined);
    try {
      setNotice(await work());
      loadNoticed();
      load();
    } catch (error) {
      setProblem(refused(error));
    } finally {
      setBusy(false);
    }
  };

  const act = async (
    decision: Decision,
    action: CardAction,
    input?: { reason?: string; text?: string; form?: CardForm },
    card?: Card,
  ) => {
    setBusy(true);
    setProblem(undefined);
    setFailure(undefined);
    setRefusedCard(undefined);
    try {
      // An approval that needs a step-up (a payment list's, V-17) asks the person to confirm it is them, then runs again.
      const reply = await stepUp.run(() => api.act(decision, action, input));
      // A card that emails a customer: the email waits its undo window on the server, unless the reply says none goes.
      const email = action === "confirm" && card ? emailOf(card, reply) : undefined;
      if (email) setEmailing(email);
      load();
    } catch (error) {
      // A press on a decision's own card is refused on that card, left as it stands (#1006).
      if (card) setRefusedCard({ workItem: decision.workItem, failure: nameFailure(error, w) });
      else setFailure(nameFailure(error, w));
      // It may have moved on meanwhile: show the queue as it now stands.
      load();
    } finally {
      setBusy(false);
    }
  };

  if (!today) return problem ? <p role="alert">{problem}</p> : <p role="status">{w.todayLoading}</p>;
  return (
    <>
      <div className="hello">
        <h2>{headline(today.decisions, w)}</h2>
      </div>
      {stepUp.dialog}
      {notice ? <p role="status">{notice}</p> : null}
      {problem ? (
        <p className="alert" role="alert">
          {problem}
        </p>
      ) : null}
      {failure ? <FailureNotice failure={failure} /> : null}
      {emailing ? (
        <UndoEmail key={emailing.workItem} pending={emailing} onUndo={api.undoEmail} onGone={clearEmailing} />
      ) : null}
      {today.decisions.length > 0 ? (
        <ul className="queue" aria-label={w.todayQueue}>
          {today.decisions.map((d) => (
            <DecisionItem
              key={d.workItem}
              decision={d}
              busy={busy}
              onDone={(x, note, choice, form) =>
                void act(
                  x,
                  "done",
                  form !== undefined
                    ? { form }
                    : note !== undefined || choice !== undefined
                      ? {
                          form: {
                            ...(note === undefined ? {} : { note }),
                            ...(choice === undefined ? {} : { choice }),
                          },
                        }
                      : undefined,
                )
              }
              onAct={(x, action, input, card) => void act(x, action, input, card)}
              refusal={refusedCard?.workItem === d.workItem ? refusedCard.failure : undefined}
              onOpen={(x) => onOpenCard(x.workItem)}
              onOpenRun={onOpenRun}
            />
          ))}
        </ul>
      ) : null}
      {today.reminders && today.reminders.length > 0 ? (
        <section className="section" aria-labelledby="reminders-heading">
          <h2 id="reminders-heading">{w.todayReminders}</h2>
          <ul>
            {today.reminders.map((r) => (
              <li key={r.id}>{r.from ? w.todayReminderFrom(r.text, r.from) : r.text}</li>
            ))}
          </ul>
        </section>
      ) : null}
      {noticed.length > 0 ? (
        <section className="section" aria-labelledby="noticed-heading">
          <h2 id="noticed-heading">{w.todayNoticed}</h2>
          <Suggestions
            suggestions={noticed}
            busy={busy}
            onAccept={(s) => void answer(() => acceptSuggestion(api, s, w))}
            onDismiss={(s) => void answer(() => dismissSuggestion(api, s, w))}
          />
        </section>
      ) : null}
      <section className="section" aria-labelledby="issued-heading">
        <h2 id="issued-heading">{w.todayIssued}</h2>
        {today.issued.length === 0 ? (
          <p className="muted">{w.todayNoIssued}</p>
        ) : (
          <ul className="plain-list">
            {today.issued.map((d) => (
              <li key={d.id}>
                <span className="tag tag-good">{w.todayIssuedTag}</span>{" "}
                <a href={d.pdf} target="_blank" rel="noopener">
                  {d.object === "invoice" ? w.todayInvoice : d.object} {d.number ?? ""} {w.todayPdf}
                </a>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}
